import { useState, useEffect, Fragment } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";

import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Moment from "react-moment";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import ParticleSimDetails from "./ParticleSimDetails";
import ParticleApi from "../Api/ParticleApi";

import ProductInformation from "../Particle/LookupProductInformation";
import ParticleShortFunction from "../Particle/ParticleShortFunction";

import ShortCutParticleFunctions from "../Configuration/ShortCutParticleFunctions";

function DeviceParticleDetails(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [deviceDetails, setDeviceDetails] = useState(null);

  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    ParticleApi.getDeviceDetails(props.productId, props.coreId)
      .then((resp) => {
        if (isMounted) {
          setDeviceDetails(resp.response);
          setError(null);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.log(error);
          setError(error);
          setIsLoading(false);
        }
      });
    return () => {
      // clean the state
      isMounted = false;
      setError(null);
      setIsLoading(false);
      setDeviceDetails(null);
    };
  }, [props.coreId, props.productId]);

  if (error) {
    return <div>Error: {error.message}</div>;
  } else if (isLoading) {
    return (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
  } else if (deviceDetails) {
    const particleProductString = ProductInformation.getProductName(
      deviceDetails.product_id
    );
    return (
      <Container maxWidth="lg">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={5}>
            <Grid container spacing={3}>
              <Grid item xs={6} md={6} lg={6}>
                <h4>Details</h4>
              </Grid>
              <Grid item xs={6} md={6} lg={6}>
                {(() => {
                  const deviceHref =
                    "https://console.particle.io/" +
                    particleProductString +
                    "/devices/" +
                    props.coreId;
                  return (
                    <div style={{ float: "right", display: "flex", gap: 4 }}>
                      <Tooltip
                        title={
                          <img
                            src="/particle_logo.png"
                            alt="Particle"
                            style={{
                              width: 16,
                              height: 16,
                              display: "block",
                            }}
                          />
                        }
                        arrow
                        placement="top"
                      >
                        <IconButton
                          target="_blank"
                          href={deviceHref}
                          aria-label="Open device in Particle Console"
                          size="small"
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  );
                })()}
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <Paper>
                  <Table classes={{ root: "reactivetable" }}>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Name
                        </TableCell>
                        <TableCell data-th="Name">
                          {deviceDetails.name}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Last Seen
                        </TableCell>
                        <TableCell data-th="Last Seen">
                          {deviceDetails.last_heard ? (
                            <Moment format="YYYY-MM-DD HH:mm:ss Z">
                              {deviceDetails.last_heard}
                            </Moment>
                          ) : (
                            <div>-</div>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Product Id
                        </TableCell>
                        <TableCell data-th="Product ID">
                          {deviceDetails.product_id.toString()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Connected
                        </TableCell>
                        <TableCell data-th="Connected">
                          {deviceDetails.online.toString()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Notes
                        </TableCell>
                        <TableCell data-th="Notes">
                          {deviceDetails.notes}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          ICCID
                        </TableCell>
                        <TableCell data-th="ICCID">
                          {deviceDetails.iccid}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          OS Version
                        </TableCell>
                        <TableCell data-th="OS Version">
                          {deviceDetails.system_firmware_version}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          Groups
                        </TableCell>
                        <TableCell data-th="Groups">
                          <ul>
                            {deviceDetails.groups
                              ? deviceDetails.groups.map((row, key) => (
                                  <li key={key}>{row}</li>
                                ))
                              : null}
                          </ul>
                        </TableCell>
                      </TableRow>

                      {deviceDetails.serial_number ? (
                        <Fragment>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              Particle Serial Number
                            </TableCell>
                            <TableCell data-th="Particle Serial Numver">
                              {deviceDetails.serial_number}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              Device Type
                            </TableCell>
                            <TableCell data-th="Device Type">
                              {deviceDetails.serial_number.slice(0, 4)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              Manufacturing Facility
                            </TableCell>
                            <TableCell data-th="Manufacturing Facility">
                              {deviceDetails.serial_number.slice(4, 6)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              Date Code - Year
                            </TableCell>
                            <TableCell data-th="Date Code - Year">
                              201{deviceDetails.serial_number.slice(6, 7)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              Date Code - Week Start
                            </TableCell>
                            <TableCell data-th="Date Code - Week Start">
                              {deviceDetails.serial_number.slice(7, 9)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell component="th" scope="row">
                              Unique ID
                            </TableCell>
                            <TableCell data-th="Unique ID">
                              {deviceDetails.serial_number.slice(9, 15)}
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      ) : null}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6} lg={5}>
            <Grid container spacing={3}>
              <Grid item xs={6} md={6} lg={6}>
                <h4>Sim Details</h4>
              </Grid>
              <Grid item xs={6} md={6} lg={6}>
                {(() => {
                  const simHref =
                    "https://console.particle.io/" +
                    particleProductString +
                    "/sims/?iccid=" +
                    deviceDetails.iccid;
                  return (
                    <div style={{ float: "right", display: "flex", gap: 4 }}>
                      <Tooltip
                        title={
                          <img
                            src="/particle_logo.png"
                            alt="Particle"
                            style={{
                              width: 16,
                              height: 16,
                              display: "block",
                            }}
                          />
                        }
                        arrow
                        placement="top"
                      >
                        <IconButton
                          target="_blank"
                          href={simHref}
                          aria-label="Open SIM in Particle Console"
                          size="small"
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  );
                })()}
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <ParticleSimDetails
                  productId={deviceDetails.product_id.toString()}
                  iccid={deviceDetails.iccid}
                />{" "}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <h4>Shortcuts</h4>
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <Paper>
                  <Grid container justifyContent={"space-between"}>
                    {deviceDetails.functions &&
                      deviceDetails.functions &&
                      ShortCutParticleFunctions.map((f, i) =>
                        deviceDetails.functions.includes(f.commandFilter) &&
                        (f.deviceFilter
                          ? f.deviceFilter.includes(
                              deviceDetails.product_id.toString()
                            )
                          : true) ? (
                          <ParticleShortFunction
                            key={i}
                            name={f.name}
                            calls={f.calls}
                            coreId={props.coreId}
                            productId={deviceDetails.product_id.toString()}
                          />
                        ) : null
                      )}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    );
  } else {
    return null;
  }
}

export default DeviceParticleDetails;
